import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface PatientUser {
  id: string; // Using patient_id as id
  name: string;
  email: string;
  patient_id: string;
  psychologist_id?: string;
  organization_id?: string;
  needs_password_reset?: boolean;
}

interface PatientAuthContextType {
  patient: PatientUser | null;
  user: User | null; // Kept for type compatibility but will be null
  session: Session | null; // Kept for type compatibility but will be null
  loading: boolean;
  signIn: (
    email: string,
    password: string,
  ) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
}

// Interface for login form data to avoid 'any'
export interface PatientLoginData {
  email: string;
  password?: string;
  [key: string]: string | undefined;
}

const PatientAuthContext = createContext<PatientAuthContextType | undefined>(
  undefined,
);

export function PatientAuthProvider({ children }: { children: ReactNode }) {
  const [patient, setPatient] = useState<PatientUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore session from localStorage on mount and refresh data
  useEffect(() => {
    const restoreSession = async () => {
      const storedSession = localStorage.getItem("patient_portal_session");
      if (storedSession) {
        try {
          const parsedSession = JSON.parse(storedSession);

          if (parsedSession && parsedSession.patient_id) {
            // First, set the stored session immediately to avoid flash of loading/unauth
            setPatient(parsedSession);

            // Fetch fresh data using SECURITY DEFINER RPC to bypass RLS/PGRST116 issues
            const { data, error } = await supabase.rpc(
              "get_portal_patient_details_v15" as any,
              {
                p_patient_id: parsedSession.patient_id,
              },
            );

            if (error) {
              console.error("Erro no Supabase:", error.message);
            } else if (!data || !data.patient) {
              console.warn("Paciente não encontrado no banco de dados.");
              localStorage.removeItem("patient_portal_session");
              setPatient(null);
            } else {
              const updatedPatient: PatientUser = {
                id: data.patient.id,
                patient_id: data.patient.id,
                name: data.patient.name,
                email: data.patient.email || parsedSession.email,
                psychologist_id: data.patient.psychologist_id,
                organization_id: data.organization?.id || undefined,
              };

              setPatient(updatedPatient);
              localStorage.setItem(
                "patient_portal_session",
                JSON.stringify(updatedPatient),
              );
            }
          }
        } catch (e) {
          console.error("Error parsing patient session", e);
          localStorage.removeItem("patient_portal_session");
        }
      }
      setLoading(false);
    };

    restoreSession();
  }, []);

  const signIn = async (
    email: string,
    password: string,
  ): Promise<{ error: string | null }> => {
    try {
      const { data, error } = await supabase.rpc("verify_patient_credentials", {
        p_email: email,
        p_password: password,
      });

      if (error) {
        console.error("RPC Error details:", error);
        return { error: "Erro de conexão com o servidor" };
      }

      // Normalize response data: handle both array (standard RPC) and object (single response)
      let result = data;
      if (Array.isArray(data)) {
        if (data.length > 0) {
          result = data[0];
        } else {
          result = null;
        }
      }

      if (result && result.success) {
        const patientId = result.patientId || result.patient_id;

        // Ensure we have a valid patient ID before proceeding
        if (patientId) {
          // Use the SECURITY DEFINER RPC to fetch details, avoiding direct table RLS issues
          const { data: patientDetails } = await supabase.rpc(
            "get_portal_patient_details_v14" as any,
            {
              p_patient_id: patientId,
            },
          );

          const patientUser: PatientUser = {
            id: patientId,
            patient_id: patientId,
            name:
              patientDetails?.patient?.name ||
              result.name ||
              result.email ||
              "Paciente",
            email: result.email,
            psychologist_id: result.psychologist_id, // RPC primary return
            organization_id: patientDetails?.organization?.id || undefined,
          };

          setPatient(patientUser);
          localStorage.setItem(
            "patient_portal_session",
            JSON.stringify(patientUser),
          );
          return { error: null };
        }
      } else if (result && result.success === false) {
        console.error("Login failed:", result.error);
        return { error: result.error || "Erro desconhecido" };
      }

      console.error("Login failed: Invalid response data", data);
      return { error: "Erro desconhecido" };
    } catch (error) {
      console.error("Sign in error:", error);
      const message =
        error instanceof Error ? error.message : "Erro desconhecido";
      return { error: message || "Erro ao conectar com o servidor" };
    }
  };

  const signOut = async () => {
    try {
      // Just clear local state
      localStorage.removeItem("patient_portal_session");
      setPatient(null);
    } catch (error) {
      console.error("Sign out error:", error);
    }
  };

  const updatePassword = async (
    newPassword: string,
  ): Promise<{ error: string | null }> => {
    // This feature needs a new RPC or update to 'register_patient_access' logic for self-update.
    // For now, returning not implemented or disabled as we moved away from Supabase Auth.
    return {
      error:
        "Funcionalidade temporariamente indisponível. Contate seu psicólogo.",
    };
  };

  return (
    <PatientAuthContext.Provider
      value={{
        patient,
        user: null,
        session: null,
        loading,
        signIn,
        signOut,
        updatePassword,
      }}
    >
      {children}
    </PatientAuthContext.Provider>
  );
}

export function usePatientAuth() {
  const context = useContext(PatientAuthContext);
  if (context === undefined) {
    throw new Error("usePatientAuth must be used within a PatientAuthProvider");
  }
  return context;
}
